import { createSelector } from '@reduxjs/toolkit';

import type { AccountView, AccountsState } from './types';
import { tokensByAccounts } from './utils';

/**
 * Builds the accounts selectors for a slice mounted anywhere in the state tree.
 *
 * @param selectState - Returns the accounts slice state, e.g. `(state) => state.esTrading.accounts`.
 */
export function makeAccountsSelectors<S>(selectState: (state: S) => AccountsState) {
  const selectAccountStats = (state: S) => selectState(state).stats;
  const selectAccounts = (state: S) => selectState(state).list;
  const selectBrokenAccounts = (state: S) => selectState(state).brokenAccounts;

  return {
    /** Last WS error of the accounts commands, or `null`. */
    selectError: (state: S) => selectState(state).error,
    /** `true` while the accounts of a freshly entered section are loading. */
    selectIsAccountsLoading: (state: S) => selectState(state).requestStatus === 'pending',
    /** Raw per-exchange stats as received from the `Accounts` command. */
    selectAccountStats,
    /** Healthy accounts, flattened into view rows. */
    selectAccounts,
    /** Healthy accounts keyed by account name (id). */
    selectObjAccounts: createSelector(selectAccounts, (list): Record<string, AccountView> =>
      Object.fromEntries(list.map((account) => [account.name, account])),
    ),
    /** Accounts the backend reported as broken (errors of the `Accounts` command). */
    selectBrokenAccounts,
    /** `true` while an enable/disable request is in flight. */
    selectIsAccountUpdating: (state: S) =>
      selectState(state).resUpdateAccount?.status === 'pending',
    /** `true` once the last enable/disable request was acknowledged. */
    selectIsAccountUpdated: (state: S) => selectState(state).resUpdateAccount?.status === 'success',
    /** The last enable/disable request (account id and status), or `null`. */
    selectIsAccountUpdateStatus: (state: S) => selectState(state).resUpdateAccount,
    /** Healthy and broken accounts in one list. */
    selectJoinedAccounts: createSelector(
      selectAccounts,
      selectBrokenAccounts,
      (accounts: AccountView[], brokenAccounts: AccountView[]) =>
        accounts.concat(brokenAccounts).filter(Boolean),
    ),
    /** Token pair of every account, keyed by account id. */
    selectTokensByAccounts: createSelector(selectAccountStats, tokensByAccounts),
  };
}

/** State shape the shared (MM) slice selectors read; the reducer must be mounted at `accounts`. */
export interface Store {
  accounts: AccountsState;
}

/** Selectors of the shared (MM) accounts slice, mounted at `accounts`. */
export const {
  selectError,
  selectIsAccountsLoading,
  selectAccountStats,
  selectAccounts,
  selectObjAccounts,
  selectBrokenAccounts,
  selectIsAccountUpdating,
  selectIsAccountUpdated,
  selectIsAccountUpdateStatus,
  selectJoinedAccounts,
  selectTokensByAccounts,
} = makeAccountsSelectors((store: Store) => store.accounts);
